import React, { useCallback, useEffect, useRef } from 'react';
import { useIsMobileLayout } from '../../../hooks/useIsMobileLayout';
import { Animated, Pressable, View, Dimensions, PanResponder, DimensionValue } from 'react-native';
import { OnPromptCloseContext } from './OnPromptCloseProvider';
import { colors, zIndex } from '../../../utils/theme';
import { getSpacing } from '../../../utils/spacing';
import Stack from '../Stack';
import PressableDynamicOpacity from '../PressableDynamicOpacity';
import { FixedContentContainer } from '../FixedPositionPortalContainer';
import { useIsE2E } from '../../../hooks/useIsE2E';

export type PromptContainerOwnProps = {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    renderPromptContents: (onClose: () => void) => React.ReactNode;
    mobileMaxHeight?: string | number;
    desktopMaxHeight?: string | number;
    desktopHeight?: DimensionValue;
    mobileHeight?: DimensionValue;
    onClosed?: () => void;
    desktopWidth?: DimensionValue;
};

export type PromptContainerProps = PromptContainerOwnProps;

const IsInPromptContext = React.createContext(false);

export const useIsInPrompt = () => React.useContext(IsInPromptContext);

const PromptContainer: React.FC<PromptContainerProps> = ({
    isOpen,
    setIsOpen,
    renderPromptContents,
    mobileMaxHeight,
    desktopMaxHeight,
    onClosed,
    desktopWidth,
    desktopHeight,
    mobileHeight,
}) => {
    const isMobile = useIsMobileLayout();
    const screenHeight = Dimensions.get('window').height;
    const [isContentRendered, setIsContentRendered] = React.useState(isOpen);
    const [isPromptMounted, setIsPromptMounted] = React.useState(false);
    const isDismissBarPressed = useRef(false);
    const isE2E = useIsE2E();
    const hasBeenOpened = useRef(isOpen);
    useEffect(() => {
        if (isOpen) {
            hasBeenOpened.current = true;
        }

        if (!isOpen && hasBeenOpened.current) {
            onClosed?.();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const slideUpY = useRef(new Animated.Value(isOpen ? (isMobile ? screenHeight : 0) : screenHeight)).current;
    const opacity = useRef(new Animated.Value(isOpen ? 1 : 0)).current;

    useEffect(() => {
        // Drive mount state from a useEffect timeout instead of Animated.timing(...).start(callback).
        // The Animated callback is unreliable on RN 0.81 + React 19 (web) — it can be dropped or batched
        // such that setIsPromptMounted never fires. We still run the animation for visuals.
        const ANIMATION_DURATION = 200;
        let mountTimer: ReturnType<typeof setTimeout> | null = null;

        if (isOpen) {
            setIsContentRendered(true);
            setIsPromptMounted(false);
            Animated.timing(slideUpY, {
                toValue: 0, // Keep slide-up only for mobile
                duration: ANIMATION_DURATION,
                useNativeDriver: true,
            }).start();

            opacity.setValue(0);
            Animated.timing(opacity, {
                toValue: 1,
                duration: ANIMATION_DURATION,
                useNativeDriver: true,
            }).start();

            mountTimer = setTimeout(() => {
                setIsContentRendered(true);
                setIsPromptMounted(true);
            }, ANIMATION_DURATION);
        } else {
            Animated.timing(slideUpY, {
                toValue: screenHeight,
                duration: ANIMATION_DURATION,
                useNativeDriver: true,
            }).start();

            Animated.timing(opacity, {
                toValue: 0,
                duration: ANIMATION_DURATION,
                useNativeDriver: true,
            }).start();

            mountTimer = setTimeout(() => {
                setIsContentRendered(false);
                setIsPromptMounted(false);
            }, ANIMATION_DURATION);
        }

        return () => {
            if (mountTimer) clearTimeout(mountTimer);
        };
    }, [isOpen, screenHeight, slideUpY, opacity, isMobile]);

    const panResponder = useRef(
        PanResponder.create({
            onMoveShouldSetPanResponder: (_, gestureState) => gestureState.dy > 10,
            onPanResponderMove: (_, gestureState) => {
                if (gestureState.dy > 0 && isDismissBarPressed.current) {
                    slideUpY.setValue(gestureState.dy);
                }
            },
            onPanResponderRelease: (_, gestureState) => {
                if (!isDismissBarPressed.current) return;

                if (gestureState.dy > screenHeight / 5) {
                    setIsOpen(false);
                } else {
                    Animated.timing(slideUpY, {
                        toValue: 0,
                        duration: 200,
                        useNativeDriver: true,
                    }).start();
                }
            },
        }),
    ).current;

    const onClose = useCallback(() => {
        setIsOpen(false);
    }, [setIsOpen]);

    if (!isContentRendered) return null;

    const renderContent = () => (
        <Pressable
            onPress={(e) => {
                e.stopPropagation();
            }}
            style={{
                width: isMobile ? '100%' : desktopWidth || 310,
                maxWidth: '100%',
                maxHeight: isMobile ? mobileMaxHeight || '50%' : desktopMaxHeight || (600 as any),
                height: isMobile ? mobileHeight : desktopHeight,
            }}
        >
            <Animated.View
                testID={isPromptMounted ? 'prompt-mounted' : 'prompt-unmounted'}
                style={{
                    borderRadius: isMobile ? 20 : 5,
                    borderBottomLeftRadius: isMobile ? 0 : undefined,
                    borderBottomRightRadius: isMobile ? 0 : undefined,
                    width: '100%',
                    backgroundColor: colors.White,
                    flexGrow: 1,
                    flexShrink: 1,
                    // the slide up animation is flakey on e2e and will pin the modal to the top of the screen. I have not experienced any "real" issue with it
                    transform: isMobile && !isE2E ? [{ translateY: slideUpY }] : undefined,
                }}
                {...(isMobile ? panResponder.panHandlers : {})}
            >
                <IsInPromptContext.Provider value={true}>
                    <Stack vertical space={2} style={{ width: '100%', maxHeight: '100%', flexGrow: 1 }}>
                        {isMobile && (
                            <>
                                <PressableDynamicOpacity
                                    style={{ width: '100%', height: 20 }}
                                    onTouchStart={() => (isDismissBarPressed.current = true)}
                                    onTouchEnd={() => {
                                        // Delay for swipe away.  The callback won't closed if not "touched"
                                        setTimeout(() => (isDismissBarPressed.current = false), 500);
                                    }}
                                >
                                    <View
                                        style={{
                                            width: '100%',
                                            display: 'flex',
                                            flexDirection: 'row',
                                            justifyContent: 'center',
                                            padding: getSpacing(4),
                                        }}
                                    >
                                        <View
                                            style={{
                                                height: 5,
                                                borderRadius: 10,
                                                width: 114,
                                                backgroundColor: colors['Drag-Indicator'],
                                            }}
                                        />
                                    </View>
                                </PressableDynamicOpacity>
                            </>
                        )}
                        <View
                            style={{
                                width: '100%',
                                flexGrow: 1,
                                flexShrink: 1,
                            }}
                        >
                            {renderPromptContents(onClose)}
                        </View>
                    </Stack>
                </IsInPromptContext.Provider>
            </Animated.View>
        </Pressable>
    );

    return (
        <FixedContentContainer>
            <OnPromptCloseContext.Provider value={onClose}>
                <Pressable
                    onPress={onClose}
                    style={{
                        width: '100%',
                        height: '100%',
                        overflow: 'hidden',
                    }}
                    testID="prompt-container-pressable"
                >
                    <Animated.View
                        style={{
                            opacity,
                            width: '100%',
                            height: '100%',
                            justifyContent: isMobile ? 'flex-end' : 'center',
                            alignContent: 'center',
                            alignItems: 'center',
                            backgroundColor: 'rgba(0, 0, 0, .4)',
                            display: 'flex',
                            zIndex: zIndex.prompt,
                        }}
                    >
                        {renderContent()}
                    </Animated.View>
                </Pressable>
            </OnPromptCloseContext.Provider>
        </FixedContentContainer>
    );
};

export default React.memo(PromptContainer);
